import React, { useState, useEffect } from 'react';
import { useAxios } from '../../core/_axios';
import { useNavigate } from 'react-router-dom';
import { triggerEmailFollowUpChanged } from '../../hooks/useEmailFollowUpEnabled';
import { Account } from '../../core/_models';

interface AccountEmailSettingsProps {
    account: Account;
    setAccount: (account: Account) => void;
    isForCurrentAccount?: boolean;
}

const AccountEmailSettings: React.FC<AccountEmailSettingsProps> = ({ account, setAccount, isForCurrentAccount }) => {
    const axios = useAxios();
    const navigate = useNavigate();
    const [disabling, setDisabling] = useState(false);
    const [enabling, setEnabling] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [originalEmailAddress, setOriginalEmailAddress] = useState('');
    const [suggestedEmail, setSuggestedEmail] = useState('');
    const [updatingEmail, setUpdatingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');
    const [editingEmail, setEditingEmail] = useState(false);
    
    // Signature settings
    const [signatureEnabled, setSignatureEnabled] = useState(true);
    const [emailSignature, setEmailSignature] = useState('');
    const [originalSignature, setOriginalSignature] = useState('');
    const [editingSignature, setEditingSignature] = useState(false);
    const [updatingSignature, setUpdatingSignature] = useState(false);
    const [signatureError, setSignatureError] = useState('');

    const isEmailFollowUpEnabled = account?.public_data?.email_follow_up_enabled === true;

    useEffect(() => {
        if (account) {
            loadEmailSettings();
        }
    }, [account?.unique_identifier]);

    const loadEmailSettings = async () => {
        try {
            // Get email settings from dedicated endpoint with account header
            const response = await axios.get('/api/v1/app/authenticated/current-account/email-settings', {
                headers: {
                    'X-Account-Unique-Id': account.unique_identifier
                }
            });
            
            if (response.data.success) {
                const settings = response.data.data;
                const currentEmail = settings.email_address || '';
                setEmailAddress(currentEmail);
                setOriginalEmailAddress(currentEmail);
                
                // Load signature settings
                setSignatureEnabled(settings.email_signature_enabled);
                setEmailSignature(settings.email_signature || '');
                setOriginalSignature(settings.email_signature || '');
                
                // Get suggested email if none exists
                if (!currentEmail) {
                    try {
                        const suggestResponse = await axios.get('/api/v1/app/authenticated/current-account/suggest-email-address', {
                            headers: {
                                'X-Account-Unique-Id': account.unique_identifier
                            }
                        });
                        if (suggestResponse.data.success) {
                            setSuggestedEmail(suggestResponse.data.data.suggested_email);
                        }
                    } catch (err) {
                        console.error('Failed to get suggested email:', err);
                    }
                }
            }
        } catch (err) {
            console.error('Failed to load email settings:', err);
        }
    };

    const handleEnableEmailFollowUp = async () => {
        if (!emailAddress) {
            setEmailError('Please set an email address first');
            return;
        }
        
        setEnabling(true);
        
        try {
            const response = await axios.post('/api/v1/app/authenticated/current-account/update-current-account', {
                account_name: account.name,
                public_data_email_follow_up_enabled: true
            }, {
                headers: {
                    'X-Account-Unique-Id': account.unique_identifier
                }
            });
            
            if (response.data) {
                const updatedAccount = response.data.data?.account || response.data.account;
                if (updatedAccount) {
                    setAccount(updatedAccount);
                    triggerEmailFollowUpChanged(true);
                    setEmailSuccess('Email follow-up enabled successfully!');
                    setTimeout(() => setEmailSuccess(''), 3000);
                }
            }
        } catch (err) {
            console.error('Failed to enable email follow-up:', err);
            setEmailError('Failed to enable email follow-up. Please try again.');
        } finally {
            setEnabling(false);
        }
    };

    const handleUpdateEmailAddress = async () => {
        if (!emailAddress || emailAddress === originalEmailAddress) return;
        
        // Basic validation
        if (!emailAddress.match(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/) || emailAddress.length > 40) {
            setEmailError('Email address must be lowercase alphanumeric with hyphens, 3-40 characters');
            return;
        }
        
        setUpdatingEmail(true);
        setEmailError('');
        setEmailSuccess('');
        
        try {
            const response = await axios.post('/api/v1/app/authenticated/current-account/update-email-address', {
                email_address: emailAddress
            }, {
                headers: {
                    'X-Account-Unique-Id': account.unique_identifier
                }
            });
            
            if (response.data.success) {
                const updatedAccount = response.data.data.account;
                setOriginalEmailAddress(updatedAccount.email_address);
                setEmailSuccess('Email address updated successfully!');
                setEditingEmail(false);
                setAccount(updatedAccount);
                
                // Clear success message after 3 seconds
                setTimeout(() => setEmailSuccess(''), 3000);
            }
        } catch (err: any) {
            console.error('Failed to update email address:', err);
            if (err.response?.data?.errors?.email_address) {
                setEmailError(err.response.data.errors.email_address[0]);
            } else {
                setEmailError('Failed to update email address. Please try again.');
            }
        } finally {
            setUpdatingEmail(false);
        }
    };

    const handleUpdateSignature = async () => {
        if (emailSignature === originalSignature && signatureEnabled === (originalSignature !== '')) return;
        
        setUpdatingSignature(true);
        setSignatureError('');
        
        try {
            // Use new dedicated email settings endpoint
            const response = await axios.post('/api/v1/app/authenticated/current-account/email-settings', {
                email_signature_enabled: signatureEnabled,
                email_signature: signatureEnabled ? emailSignature.trim() : ''
            }, {
                headers: {
                    'X-Account-Unique-Id': account.unique_identifier
                }
            });
            
            if (response.data.success) {
                setOriginalSignature(emailSignature);
                setEmailSuccess('Signature settings updated successfully!');
                setEditingSignature(false);
                
                // Clear success message after 3 seconds
                setTimeout(() => setEmailSuccess(''), 3000);
            }
        } catch (err) {
            console.error('Failed to update signature:', err);
            setSignatureError('Failed to update signature settings. Please try again.');
        } finally {
            setUpdatingSignature(false);
        }
    };

    const handleDisableEmailFollowUp = async () => {
        setShowConfirmDialog(false);
        setDisabling(true);
        
        try {
            const response = await axios.post('/api/v1/app/authenticated/current-account/update-current-account', {
                account_name: account.name,
                public_data_email_follow_up_enabled: false
            }, {
                headers: {
                    'X-Account-Unique-Id': account.unique_identifier
                }
            });
            
            if (response.data) {
                const updatedAccount = response.data.data?.account || response.data.account;
                if (updatedAccount) {
                    setAccount(updatedAccount);
                    triggerEmailFollowUpChanged(false);
                    setEmailSuccess('Email follow-up disabled successfully!');
                    setTimeout(() => setEmailSuccess(''), 3000);
                }
            }
        } catch (err) {
            console.error('Failed to disable email follow-up:', err);
            setEmailError('Failed to disable email follow-up. Please try again.');
        } finally {
            setDisabling(false);
        }
    };

    const domain = window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok') 
        ? 'dev.mail.signatureguestbook.com' 
        : 'mail.signatureguestbook.com';

    if (!isEmailFollowUpEnabled) {
        return (
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Email Campaign Settings</h3>
                </div>
                <div className="card-body">
                    <div className="mb-6">
                        <h4 className="text-base font-semibold text-gray-900 mb-2">Email Follow-up Status</h4>
                        <p className="text-sm text-gray-600 mb-4">
                            Email follow-up campaigns are currently <span className="font-semibold text-red-600">disabled</span> for this account.
                        </p>
                        <p className="text-sm text-gray-600 mb-6">
                            Enable email follow-ups to automatically send personalized messages to guestbook signers.
                        </p>
                    </div>
                    
                    {!originalEmailAddress && (
                        <div className="mb-6">
                            <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 mb-2">
                                Set Email Address First
                            </label>
                            <div className="flex">
                                <input
                                    type="text"
                                    id="emailAddress"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder={suggestedEmail || "Enter email address"}
                                    value={emailAddress}
                                    onChange={(e) => {
                                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                                        setEmailAddress(value);
                                        setEmailError('');
                                    }}
                                    maxLength={40}
                                />
                                <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 rounded-r-md text-gray-500 text-sm">
                                    @{domain}
                                </span>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                {emailAddress ? (
                                    <>This will be your reply-to address: <strong>{emailAddress}@{domain}</strong></>
                                ) : suggestedEmail ? (
                                    <>Suggested: <strong>{suggestedEmail}</strong></>
                                ) : (
                                    'Use lowercase letters, numbers, and hyphens only (3-40 characters)'
                                )}
                            </p>
                            
                            {suggestedEmail && !emailAddress && (
                                <button
                                    onClick={() => {
                                        setEmailAddress(suggestedEmail);
                                        setEmailError('');
                                    }}
                                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                                >
                                    Use suggested email
                                </button>
                            )}
                        </div>
                    )}
                    
                    {emailError && (
                        <div className="alert alert-danger mb-4" role="alert">
                            {emailError}
                        </div>
                    )}
                    
                    {emailSuccess && (
                        <div className="alert alert-success mb-4" role="alert">
                            {emailSuccess}
                        </div>
                    )}
                    
                    <button
                        onClick={originalEmailAddress ? handleEnableEmailFollowUp : handleUpdateEmailAddress}
                        disabled={enabling || updatingEmail || (!originalEmailAddress && !emailAddress)}
                        className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {enabling || updatingEmail ? (
                            <>
                                <i className="ki-filled ki-loading mr-2 animate-spin"></i>
                                {originalEmailAddress ? 'Enabling...' : 'Setting Email Address...'}
                            </>
                        ) : (
                            <>
                                <i className="ki-filled ki-check-circle mr-2"></i>
                                {originalEmailAddress ? 'Enable Email Follow-ups' : 'Set Email Address & Enable'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Email Campaign Settings</h3>
            </div>
            <div className="card-body">
                <div className="mb-6">
                    <h4 className="text-base font-semibold text-gray-900 mb-2">Email Follow-up Status</h4>
                    <p className="text-sm text-gray-600 mb-4">
                        Email follow-up campaigns are currently <span className="font-semibold text-green-600">enabled</span> for this account.
                    </p>
                </div>

                <div className="border-t pt-6 mb-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h4 className="text-base font-semibold text-gray-900 mb-2">Reply-To Email Address</h4>
                            <p className="text-sm text-gray-600">
                                This email address will be used as the reply-to address for all email campaigns sent from this account.
                            </p>
                        </div>
                    </div>
                    
                    {emailError && (
                        <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                            {emailError}
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={() => setEmailError('')}
                                aria-label="Close"
                            ></button>
                        </div>
                    )}
                    
                    {emailSuccess && (
                        <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
                            {emailSuccess}
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={() => setEmailSuccess('')}
                                aria-label="Close"
                            ></button>
                        </div>
                    )}

                    {!editingEmail ? (
                        <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Current email address:</p>
                                <p className="text-lg font-medium text-gray-900">
                                    {originalEmailAddress ? `${originalEmailAddress}@${domain}` : 'Not set'}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setEditingEmail(true);
                                    setEmailError('');
                                }}
                                className="text-primary hover:text-primary-dark underline text-sm font-medium"
                            >
                                Edit
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="mb-4">
                                <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 mb-2">
                                    Your Email Address
                                </label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        id="emailAddress"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder={suggestedEmail || "Enter email address"}
                                        value={emailAddress}
                                        onChange={(e) => {
                                            const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                                            setEmailAddress(value);
                                            setEmailError('');
                                        }}
                                        maxLength={40}
                                    />
                                    <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 rounded-r-md text-gray-500 text-sm">
                                        @{domain}
                                    </span>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    {emailAddress ? (
                                        <>This will be your reply-to address: <strong>{emailAddress}@{domain}</strong></>
                                    ) : suggestedEmail && !originalEmailAddress ? (
                                        <>Suggested: <strong>{suggestedEmail}</strong></>
                                    ) : (
                                        'Use lowercase letters, numbers, and hyphens only (3-40 characters)'
                                    )}
                                </p>
                            </div>
                            
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setEditingEmail(false);
                                        setEmailAddress(originalEmailAddress);
                                        setEmailError('');
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                {suggestedEmail && !originalEmailAddress && (
                                    <button
                                        onClick={() => {
                                            setEmailAddress(suggestedEmail);
                                            setEmailError('');
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                                    >
                                        Use Suggested
                                    </button>
                                )}
                                <button
                                    onClick={handleUpdateEmailAddress}
                                    disabled={updatingEmail || !emailAddress || emailAddress === originalEmailAddress}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {updatingEmail ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Email Address'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t pt-6 mb-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h4 className="text-base font-semibold text-gray-900 mb-2">Email Signature</h4>
                            <p className="text-sm text-gray-600">
                                This signature will be automatically added to the end of all email campaigns.
                            </p>
                        </div>
                    </div>
                    
                    {signatureError && (
                        <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                            {signatureError}
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={() => setSignatureError('')}
                                aria-label="Close"
                            ></button>
                        </div>
                    )}

                    {!editingSignature ? (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="signatureEnabled"
                                        checked={signatureEnabled}
                                        disabled={true}
                                        className="mr-2"
                                    />
                                    <label htmlFor="signatureEnabled" className="text-sm font-medium text-gray-700">
                                        {signatureEnabled ? 'Signature enabled' : 'Signature disabled'}
                                    </label>
                                </div>
                                <button
                                    onClick={() => {
                                        setEditingSignature(true);
                                        setSignatureError('');
                                        setEmailSuccess('');
                                    }}
                                    className="text-primary hover:text-primary-dark underline text-sm font-medium"
                                >
                                    Edit
                                </button>
                            </div>
                            {signatureEnabled && originalSignature && (
                                <div className="mt-3 p-3 bg-white border border-gray-200 rounded">
                                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{originalSignature}</pre>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="mb-4">
                                <div className="flex items-center mb-3">
                                    <input
                                        type="checkbox"
                                        id="signatureEnabledEdit"
                                        checked={signatureEnabled}
                                        onChange={(e) => setSignatureEnabled(e.target.checked)}
                                        className="mr-2"
                                    />
                                    <label htmlFor="signatureEnabledEdit" className="text-sm font-medium text-gray-700">
                                        Add signature to emails
                                    </label>
                                </div>
                                
                                {signatureEnabled && (
                                    <>
                                        <label htmlFor="emailSignature" className="block text-sm font-medium text-gray-700 mb-2">
                                            Signature Text
                                        </label>
                                        <textarea
                                            id="emailSignature"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                            placeholder={`The ${account.name} Team`}
                                            value={emailSignature}
                                            onChange={(e) => setEmailSignature(e.target.value)}
                                            rows={3}
                                        />
                                        <p className="mt-2 text-xs text-gray-500">
                                            This will appear at the end of your emails, after the closing (e.g., "With gratitude,")
                                        </p>
                                    </>
                                )}
                            </div>
                            
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setEditingSignature(false);
                                        setEmailSignature(originalSignature);
                                        setSignatureEnabled(originalSignature !== '');
                                        setSignatureError('');
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateSignature}
                                    disabled={updatingSignature}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {updatingSignature ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Signature'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t pt-6">
                    <h4 className="text-base font-semibold text-gray-900 mb-2">Disable Email Follow-ups</h4>
                    <p className="text-sm text-gray-600 mb-4">
                        Disabling email follow-ups will stop all automated email campaigns. You can re-enable this feature at any time.
                    </p>
                    
                    <button
                        onClick={() => setShowConfirmDialog(true)}
                        disabled={disabling}
                        className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <i className="ki-filled ki-cross-circle mr-2"></i>
                        Disable Email Follow-ups
                    </button>
                </div>

                {/* Confirmation Dialog */}
                {showConfirmDialog && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <div className="flex items-center mb-4">
                                    <i className="ki-filled ki-information-5 text-3xl text-yellow-500 mr-3"></i>
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                                        Confirm Disable Email Follow-ups
                                    </h3>
                                </div>
                                <div className="mt-2 px-7 py-3">
                                    <p className="text-sm text-gray-500">
                                        Are you sure you want to disable email follow-ups? This will stop all automated email campaigns for this account.
                                    </p>
                                </div>
                                <div className="items-center px-4 py-3 flex justify-end gap-2">
                                    <button
                                        onClick={() => setShowConfirmDialog(false)}
                                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDisableEmailFollowUp}
                                        disabled={disabling}
                                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                    >
                                        {disabling ? (
                                            <>
                                                <i className="ki-filled ki-loading mr-2 animate-spin"></i>
                                                Disabling...
                                            </>
                                        ) : (
                                            'Yes, Disable'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountEmailSettings;